import { useContext, useState } from 'react';

import cn from 'classnames/bind';
import Head from 'next/head';
import {
  Col, Container, Row, SEO, TypographyPage,
} from 'ui';
import { Character, CharacterPageBackground } from 'ui/characters';
import { Context as LayoutContext } from 'ui/containers/LayoutContext';
import PageColorProvider from 'ui/containers/PageColorContext';
import BlogContent from 'ui/layouts/blog/BlogContent';
import ExploreBlogTypesMobileSection, {defaultColumnProps} from 'ui/layouts/ExploreBlogTypesMobileSection';
import ExploreBlogTypesSection from 'ui/layouts/ExploreBlogTypesSection';
import JoinUsSection from 'ui/layouts/JoinUsSection';
import PageContentMenu from 'ui/layouts/PageContentMenuTree';
import SectionObserver from 'ui/layouts/SectionObserver';
import SharingBlock from 'ui/layouts/SharingBlock';
import SinglePersonalityHeader from 'ui/layouts/SinglePersonalityHeader';
import StickyColumn from 'ui/layouts/StickyColumn';
import StickyHeader from 'ui/layouts/StickyHeader';
import { getSEOKeywords } from 'utils';
import { flattenTree, generateTreeForPost } from 'utils/blog';
import { mapGroupToColor, typesArray } from 'utils/personalities';
import { capitalize } from 'utils/string';

import s from './PersonalityPost.module.scss';

const cx = cn.bind(s);

const PersonalityType = ({
  type, group, gender, jsonLd, title, article, menu, exploreThisTypeItems, isLanding, layout: Layout,
}) => {
  const color = mapGroupToColor[group];
  const { breakpoint } = useContext(LayoutContext);
  const isMobile = breakpoint === 'xs' || breakpoint === 'sm' || breakpoint === 'md';
  const [active, setActive] = useState(menu?.key);
  const seo = article.seo || {};
  const menuItems = menu?.children || [];
  const hasExploreThisType = !!exploreThisTypeItems?.length;
  const hasNavigationSidebar = !!(menu || hasExploreThisType);
  return (
    <>
      {jsonLd && <Head>
        <script
          type="application/ld+json"
          key="product-jsonld"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd)
          }}
        />
      </Head>}
      <SEO
        title={seo?.seo_title || `The ${type.toUpperCase()} Personality Type: All About the ${capitalize(
          title,
        )}`}
        description={seo?.meta_description || `The ${type.toUpperCase()} personality type (also known as the "${title}") is quiet, idealistic, and creative. Learn all about INFP traits, strengths, weaknesses, and more.`}
        image={seo?.image}
        keywords={getSEOKeywords(seo)}
      />
      <SectionObserver
        key={article.title}
        bottom
        hasBlogNavigation
        items={menuItems}
        onActiveChange={setActive}
      >
        <Layout
          headerProps={{
            filled: true,
            append:
            isMobile ? (
              <StickyHeader active={active}
                color="mint"
                isDense
                items={flattenTree(menuItems)} />
            ) : null,
          }}
        >
          <SinglePersonalityHeader
            color={color}
            title={capitalize(title)}
            info={type}
            character={<Character type={type} gender={gender} />}
            characterExtra={group && <CharacterPageBackground group={group} />}
          />
          <Container className={cx('content')}>
            <Row>
              <Col {...defaultColumnProps} lg={3} className={cx('menuColumn')}>
                {hasNavigationSidebar && (
                  <StickyColumn className={cx('menuColumn')}>
                    {!!menu && (
                      <PageContentMenu items={menuItems} active={active} />
                    )}
                    {hasExploreThisType && (
                      <ExploreBlogTypesSection items={exploreThisTypeItems} isLanding={isLanding} />
                    )}
                  </StickyColumn>
                )}
              </Col>
              {article && article.body ? (
                <PageColorProvider color={color}>
                  <Col
                    xs={12}
                    md={{ offset: 1, span: 10 }}
                    lg={{ offset: 0, span: 8 }}
                    xl={7}
                  >
                    <TypographyPage dense color={color}>
                      <BlogContent body={article.body} />
                    </TypographyPage>
                  </Col>
                </PageColorProvider>
              ) : null}
            </Row>
            <div className="flex justify-center mt-8">
              <SharingBlock
                className="mt-8"
                title={`${capitalize(type)} Personality Type`}
                url={`${process.env.NEXT_PUBLIC_HOST}/personality-type/${type}`}
              />
            </div>
          </Container>
          {hasExploreThisType && (
            <div className={cx('exploreTypeSection')}>
              <ExploreBlogTypesMobileSection items={exploreThisTypeItems} isLanding={isLanding} />
            </div>
          )}
          <JoinUsSection title="Don’t yet know your personality? Try our free test!" href={'/free-personality-test'} />
        </Layout>
      </SectionObserver>
    </>
  );
};

PersonalityType.getLayout = (children) => children;

const getModel = async (isLanding) => {
  if (isLanding) {
    const { default: model } = await import(
      'api/models/LandingPersonalityPost'
    );
    return model;
  }
  const { default: model } = await import('api/models/PersonalityType');
  return model;
};
export async function getStaticProps(context) {
  const defaultType = process.env.NEXT_PUBLIC_LANDING_TYPE;
  const { type = defaultType, articleSlug } = context.params || {};
  const articles = await (async () => {
    const model = await getModel(!!defaultType);
    const method = defaultType ? 'getArticles' : 'getArticlesByType';
    return model[method](type);
  })();
  let exploreThisTypeItems = null
  if (!defaultType) {
    try {
      const { default: blogModel } = await import('api/models/BlogPost');
      exploreThisTypeItems = await blogModel.getExplorePosts(type)
    // eslint-disable-next-line no-empty
    } catch {}
  }
  const articleIndex = articleSlug
    ? articles.findIndex((a) => a?.slug === articleSlug)
    : 0;
  const article = articles[articleIndex] || {};
  const localTypeData = typesArray.find((t) => t.slug === type);
  const group = article.type?.type || localTypeData?.group?.title || '';
  const title = article.type?.title || localTypeData?.title || '';

  const gender = article?.type?.defaultGender || localTypeData?.defaultGender || '';
  const options = { isCurrent: true, isInitial: true, levels: [2, 3, 4] };
  let menu = null
  if (!defaultType) {
    try {
      menu = generateTreeForPost(article, options) || null;
    // eslint-disable-next-line no-empty
    } catch {}
  }
  const props = {
    type,
    articles,
    isLanding: !!defaultType,
    group,
    gender,
    title,
    article,
    exploreThisTypeItems,
    menu
  }
  return {
    props,
    revalidate: 60,
  };
}

export async function getStaticPaths() {
  const model = await getModel(!!process.env.NEXT_PUBLIC_LANDING_TYPE);
  const articles = await model.getArticles(process.env.NEXT_PUBLIC_LANDING_TYPE);
  return {
    paths: articles.map((article) => ({
      params: {
        type: article?.type?.slug,
        articleSlug: article.slug,
      },
    })),
    fallback: 'blocking',
  };
}

export default PersonalityType;
